<template>
  <div :class="reportViewClass">
    <GridPanelHeader
      :title="templateData?.name"
      :grid-ref="$refs[reportGridRefKey]"
      :show-add="false"
      :show-filter="basicInfoItems.length > 0"
      :show-columns="true"
      :show-export="true"
      :show-refresh="true"
      :download-options="headerDownloadOptions"
      @filter="openFilterPanel"
      @refresh="refreshDataGrid"
      @export-option-selected="onHeaderExportOptionSelected"
    >
      <template #back_button_action>
        <DxButton class="back-button" icon="arrowleft" @click="backToList" />
      </template>
    </GridPanelHeader>

    <div v-if="isHarvestYieldComparisonReport" class="report-summary-cards">
      <div v-for="card in summaryCards" :key="card.key" class="report-summary-card" :class="card.className">
        <span class="report-summary-card__label">{{ card.label }}</span>
        <span class="report-summary-card__value">{{ card.value }}</span>
      </div>
    </div>

    <div v-if="rowLegendItems.length" class="report-row-legend">
      <div v-for="legend in rowLegendItems" :key="legend.key" class="report-row-legend__item">
        <span class="report-row-legend__dot" :class="legend.colorClass"></span>
        <span class="report-row-legend__label">{{ legend.label }}</span>
      </div>
    </div>

    <GridFilter
      :visible.sync="filterPanelVisible"
      :title="$t('common.filter')"
      :grid-ref="$refs[reportGridRefKey]"
      @apply="applyFilters"
      @reset="resetFilters"
    >
      <DxForm
        ref="form"
        class="report-filter-form"
        :show-colon-after-label="true"
        :col-count="1"
        :form-data="formData"
        label-location="top"
        label-mode="outside"
        :on-field-data-changed="(evt) => onFieldChange(evt.dataField, evt.value)"
        v-if="basicInfoItems.length > 0"
      >
        <DxFormItem
          v-for="item in basicInfoItems"
          :key="item.dataField"
          class="report-filter-form-item"
          :data-field="item.dataField"
          :editor-type="item.editorType"
          :editor-options="item.editorOptions"
        >
          <dx-label v-if="item.label" :text="item.label" />
          <dx-validation-rule v-for="rule in item.validators || []" :key="`${item.dataField}-${rule.type}`" :type="rule.type" />
        </DxFormItem>
      </DxForm>
    </GridFilter>

    <DxStandaloneLoadPanel
      :visible="isInitialLoading || isGridLoading"
      :show-indicator="true"
      :show-pane="true"
      shading-color="rgba(255,255,255,0.7)"
      :message="$t('common.please_wait')"
    />

    <DxDataGrid
      :ref="reportGridRefKey"
      :data-source="report_data_source"
      :column-min-width="200"
      :show-column-lines="true"
      :show-row-lines="true"
      :show-borders="true"
      :column-auto-width="true"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :element-attr="gridAttributes"
      :columns="gridColumns"
      :remote-operations="remoteOperations"
      @exporting="onExporting"
      @row-prepared="onRowPrepared"
      @content-ready="onGridContentReady"
      @option-changed="onGridOptionChanged"
      width="100%"
      :row-height="50"
    >
      <DxPaging :page-size="40" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="reportPageSizes"
        display-mode="compact"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :info-text="$t('common.page_info')"
      />
      <DxEditing :allow-adding="false" :allow-updating="false" :allow-deleting="false" :use-icons="true" mode="row" />
      <DxHeaderFilter :visible="!isServerPagedReport" />
      <DxFilterRow :visible="true" apply-filter="auto" />
      <DxSorting :mode="isServerPagedReport ? 'single' : 'multiple'" />
      <DxSelection mode="multiple" show-check-boxes-mode="always" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxExport :enabled="false" :allow-export-selected-data="true" />
      <DxGroupPanel :visible="!isServerPagedReport" :empty-panel-text="$t('common.group_panel_placeholder')" />
      <DxGrouping :auto-expand-all="!isServerPagedReport" />
      <DxScrolling column-rendering-mode="virtual" />
      <DxSummary v-if="!isServerPagedReport" :calculate-custom-summary="onCalculateCustomSummary">
        <DxTotalItem
          v-for="item in aggregatedColumns.filter((g) => g.show_aggregate_in_footer)"
          :key="`total-${item.name}`"
          :column="item.summary_type === 'custom' ? undefined : item.column"
          :name="item.name"
          :show-in-column="item.column"
          :summary-type="item.summary_type"
          :value-format="item.value_format"
        />
        <DxGroupItem
          v-for="item in aggregatedColumns.filter((g) => g.show_aggregate_in_group)"
          :key="`group-${item.name}`"
          :column="item.summary_type === 'custom' ? undefined : item.column"
          :name="item.name"
          :show-in-column="item.column"
          :summary-type="item.summary_type"
          :value-format="item.value_format"
          :show-in-group-footer="true"
        />
      </DxSummary>
    </DxDataGrid>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  DxDataGrid,
  DxPaging,
  DxPager,
  DxEditing,
  DxHeaderFilter,
  DxFilterRow,
  DxSelection,
  DxColumnChooser,
  DxExport,
  DxGrouping,
  DxScrolling,
  DxGroupPanel,
  DxSummary,
  DxTotalItem,
  DxGroupItem,
  DxSorting,
} from "devextreme-vue/data-grid";
import { DxButton } from "devextreme-vue/button";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { DxForm, DxItem as DxFormItem, DxLabel, DxValidationRule } from "devextreme-vue/form";
import query from "devextreme/data/query";
import CustomStore from "devextreme/data/custom_store";
import moment from "moment";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import GridFilter from "@/components/DxGrid/GridFilter.vue";
import {
  REPORT_CODE_CONFIGS,
  getRowStatusByReportCode,
  buildSummaryCardsByReportCode,
  buildRowLegendItemsByReportCode,
  ensureReportStylesLoaded,
} from "./reportGridView/reportSpecialLogic";
import { DxLoadPanel as DxStandaloneLoadPanel } from "devextreme-vue/load-panel";

export default {
  name: "ReportGridView",
  props: {
    showModule: {
      type: Boolean,
      default: false,
    },
    templateData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      formData: {},
      basicInfoItems: [],
      reportFiltersMeta: [],
      reportGridRefKey: "report-grid",
      pageSizes: [40, 100, 500, 99999],
      report_dataset: [],
      report_data_source: [],
      summaryDataset: null,
      summaryDatasetKey: null,
      summaryRequestKey: null,
      filterPanelVisible: false,
      gridColumns: [],
      aggregatedColumns: [],
      isGridLoading: false,
      isInitialLoading: false,
      pendingContentReadyHide: false,
    };
  },
  watch: {
    showModule: {
      handler(newVal) {
        if (newVal) {
          this.loadReportData();
        }
      },
      deep: true,
    },
    "templateData.code": {
      handler(newCode, oldCode) {
        if (newCode === oldCode) {
          return;
        }

        this.applyReportSpecificLogic(newCode, oldCode);
      },
      immediate: true,
    },
    report_dataset() {
      this.$nextTick(() => {
        this.updateSummaryDatasetFromGrid();
      });
    },
  },
  components: {
    DxDataGrid,
    DxPaging,
    DxPager,
    DxEditing,
    DxHeaderFilter,
    DxFilterRow,
    DxSelection,
    DxColumnChooser,
    DxExport,
    DxButton,
    DxGrouping,
    DxScrolling,
    DxGroupPanel,
    DxSummary,
    DxTotalItem,
    DxGroupItem,
    DxSorting,
    DxForm,
    DxFormItem,
    DxLabel,
    DxValidationRule,
    GridPanelHeader,
    GridFilter,
    DxStandaloneLoadPanel,
  },
  computed: {
    ...mapGetters({
      getDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
      company: "company/company",
      departments: "company/departments",
      seasons: "seasons/seasons",
      cultures: "works-cultures/cultures",
    }),
    reportGrid() {
      return this.$refs[this.reportGridRefKey] ? this.$refs[this.reportGridRefKey].instance : null;
    },
    currentReportConfig() {
      return REPORT_CODE_CONFIGS[this.templateData?.code] || {};
    },
    isServerPagedReport() {
      return false; // Server-paged reports are currently disabled
      /*return [
        "ReportDS_WorkInputs",
        "ReportDS_Parcels",
        "ReportDS_DetailedWorkReport",
        "ReportDS_Worklogs",
        "ReportDS_BonConsumPeCultura",
        "ReportDS_ConsumPeParcele",
        "ReportDS_RaportRecolta",
        "ReportDS_ProductStock",
        "ReportDS_ProductStockLot",
        "ReportDS_FisaMagazie",
        "ReportDS_PlotBasedHarvestingOperationsYieldComparison",
        "ReportDS_StockMovementsReport",
      ].includes(this.templateData?.datasource_SP);*/
    },
    reportPageSizes() {
      return this.isServerPagedReport ? [40, 100, 500] : this.pageSizes;
    },
    remoteOperations() {
      return this.isServerPagedReport ? { paging: true, filtering: true, sorting: true } : false;
    },
    reportViewClass() {
      return this.currentReportConfig.rootClass || null;
    },
    reportGridOffset() {
      return Number(this.currentReportConfig.gridHeightOffset) || 120;
    },
    gridAttributes() {
      return {
        class: "report-data-grid",
        style: `--report-grid-offset: ${this.reportGridOffset}px`,
      };
    },
    isHarvestYieldComparisonReport() {
      return Boolean(this.currentReportConfig.showSummaryCards);
    },
    summarySourceRows() {
      if (Array.isArray(this.summaryDataset)) {
        return this.summaryDataset;
      }

      return Array.isArray(this.report_dataset) ? this.report_dataset : [];
    },
    summaryCards() {
      return this.isHarvestYieldComparisonReport ? this.buildSummaryCards() : [];
    },
    rowLegendItems() {
      return buildRowLegendItemsByReportCode(this.templateData?.code, {
        t: this.$t.bind(this),
      });
    },
    headerDownloadOptions() {
      const options = [
        { id: "excel_all", text: this.$t("reporting.download_options.excel_all") },
        { id: "excel_selected", text: this.$t("reporting.download_options.excel_selected") },
        { id: "csv", text: this.$t("reporting.download_options.csv") },
      ];

      if (this.templateData?.has_pdf_export && this.templateData?.pdf_download_url) {
        options.push({ id: "pdf", text: this.$t("reporting.download_options.pdf") });
      }

      return options;
    },
  },
  methods: {
    ...mapActions({
      getReportsData: "reporting/getReportsData",
      getReportColumns: "reporting/getReportColumns",
      getReportFilters: "reporting/getReportFilters",
      downloadReportPdf: "reporting/downloadReportPdf",
      getCompany: "company/getCompany",
      getDepartments: "company/getDepartments",
      getSeasons: "seasons/getSeasons",
      getCultures: "works-cultures/getCultures",
    }),
    applyReportSpecificLogic(newCode, oldCode) {
      if (newCode === oldCode) {
        return;
      }

      ensureReportStylesLoaded(newCode).catch(() => {
        // Keep view functional even if a report style chunk fails to load.
      });
    },
    resolveColumnFormat(column) {
      if (!column) {
        return undefined;
      }

      if (column.data_type === "date") {
        return this.getDefaultDateFormat || column.format_type;
      }

      return column.format_type;
    },
    onFieldChange(fieldName, value) {
      this.$set(this.formData, fieldName, value);
    },
    onGridContentReady() {
      if (this.pendingContentReadyHide) {
        this.pendingContentReadyHide = false;
        this.isGridLoading = false;
        this.isInitialLoading = false;
      }
      this.updateSummaryDatasetFromGrid();
    },
    onGridOptionChanged(e) {
      if (!this.shouldRecalculateSummary(e)) {
        return;
      }

      this.updateSummaryDatasetFromGrid();
    },
    shouldRecalculateSummary(e) {
      if (!this.isHarvestYieldComparisonReport) {
        return false;
      }

      const fullName = String(e?.fullName || "");

      return (
        fullName.includes("filterValue") ||
        fullName.includes("selectedFilterOperation") ||
        fullName.includes("filterValues") ||
        fullName.includes("searchPanel.text")
      );
    },
    updateSummaryDatasetFromGrid() {
      if (!this.isHarvestYieldComparisonReport) {
        this.summaryDataset = null;
        return;
      }

      const grid = this.reportGrid;
      const sourceRows = Array.isArray(this.report_dataset) ? this.report_dataset : [];

      if (!grid) {
        this.summaryDataset = sourceRows;
        return;
      }

      const combinedFilter = grid.getCombinedFilter(true);
      if (this.isServerPagedReport) {
        const requestKey = JSON.stringify({
          report: this.templateData?.datasource_SP,
          filters: this.buildFilterPayload(),
          gridFilter: combinedFilter,
        });

        if (requestKey === this.summaryDatasetKey || requestKey === this.summaryRequestKey) {
          return;
        }

        this.summaryRequestKey = requestKey;
        this.summaryDataset = null;
        this.getAllReportRows().then((rows) => {
          if (this.summaryRequestKey === requestKey) {
            this.summaryDataset = rows;
            this.summaryDatasetKey = requestKey;
            this.summaryRequestKey = null;
          }
        }).catch(() => {
          if (this.summaryRequestKey === requestKey) {
            this.summaryDataset = [];
            this.summaryDatasetKey = requestKey;
            this.summaryRequestKey = null;
          }
        });
        return;
      }

      if (!combinedFilter) {
        this.summaryDataset = sourceRows;
        return;
      }

      try {
        this.summaryDataset = query(sourceRows).filter(combinedFilter).toArray();
      } catch (error) {
        this.summaryDataset = sourceRows;
      }
    },
    getCurrentDate() {
      const date = new Date();
      const year = date.getFullYear();
      const month = ("0" + (date.getMonth() + 1)).slice(-2);
      const day = ("0" + date.getDate()).slice(-2);
      const hours = ("0" + date.getHours()).slice(-2);
      const minutes = ("0" + date.getMinutes()).slice(-2);
      const seconds = ("0" + date.getSeconds()).slice(-2);
      return `${year}_${month}_${day}_${hours}_${minutes}_${seconds}`;
    },
    onExporting(e) {
      if (e.format === "xlsx") {
        e.cancel = true;
        this.exportToExcel(false);
      }
    },
    onRowPrepared(e) {
      if (e.rowType === "data") {
        if (this.isHarvestYieldComparisonReport && e.data) {
          const status = this.getReportRowStatus(e.data);
          if (status?.className) {
            e.rowElement.classList.add(status.className);
          }
        }

        if (e.data.color) {
          e.rowElement.style.backgroundColor = e.data.color;
          e.rowElement.style.color = "#000000";
        }
      }
    },
    openFilterPanel() {
      this.filterPanelVisible = true;
    },
    closeFilterPanel() {
      this.filterPanelVisible = false;
    },
    applyFilters() {
      this.reloadReportDataset();
      this.closeFilterPanel();
    },
    resetFilters() {
      this.formData = {};
      this.reportFiltersMeta.forEach((filter) => {
        this.applyFilterDefaults(filter);
      });
      this.reloadReportDataset();
    },
    async onHeaderExportOptionSelected(option) {
      const optionId = option?.id;
      if (!optionId) {
        return;
      }

      if (optionId === "excel_all") {
        this.exportToExcel(false);
        return;
      }

      if (optionId === "excel_selected") {
        this.exportToExcel(true);
        return;
      }

      if (optionId === "csv") {
        await this.exportToCsv();
        return;
      }

      if (optionId === "pdf") {
        await this.downloadPdfFromServer();
      }
    },
    async exportToExcel(selectedRowsOnly = false) {
      const grid = this.reportGrid;
      if (!grid) {
        return;
      }

      if (this.isServerPagedReport && !selectedRowsOnly) {
        const rows = await this.getAllReportRows();
        const remoteWorkbook = new Workbook();
        const remoteWorksheet = remoteWorkbook.addWorksheet("Report");
        const metadataRows = this.isHarvestYieldComparisonReport ? this.buildExportMetadataRows() : [];
        metadataRows.forEach((row, index) => {
          remoteWorksheet.getCell(index + 1, 1).value = row.label;
          remoteWorksheet.getCell(index + 1, 2).value = row.value;
        });
        const columns = grid.getVisibleColumns().filter((column) => !column.command);
        remoteWorksheet.columns = columns.map((column) => ({
          header: column.caption || column.dataField,
          key: column.dataField,
          width: Math.max(12, Math.min(40, String(column.caption || column.dataField).length + 2)),
        }));
        if (metadataRows.length) {
          remoteWorksheet.addRow([]);
        }
        rows.forEach((row) => {
          const rowData = {};
          columns.forEach((column) => {
            rowData[column.dataField] = row[column.dataField];
          });
          remoteWorksheet.addRow(rowData);
        });

        const remoteBuffer = await remoteWorkbook.xlsx.writeBuffer();
        saveAs(new Blob([remoteBuffer], { type: "application/octet-stream" }), `${this.templateData.code}_${this.getCurrentDate()}.xlsx`);
        return;
      }

      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Report");
      const metaRows = this.isHarvestYieldComparisonReport ? this.buildExportMetadataRows() : [];

      metaRows.forEach((row, index) => {
        worksheet.getCell(index + 1, 1).value = row.label;
        worksheet.getCell(index + 1, 2).value = row.value;
      });

      await exportDataGrid({
        component: grid,
        worksheet,
        selectedRowsOnly,
        autoFilterEnabled: true,
        topLeftCell: {
          row: metaRows.length + 2,
          column: 1,
        },
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer], { type: "application/octet-stream" }), `${this.templateData.code}_${this.getCurrentDate()}.xlsx`);
    },
    async downloadPdfFromServer() {
      if (!this.templateData?.pdf_download_url) {
        return;
      }

      const filters = this.buildFilterPayload();
      await this.downloadReportPdf({
        url: this.templateData.pdf_download_url,
        filters,
        filename: `${this.templateData.code}_${this.getCurrentDate()}.pdf`,
        type: "application/pdf",
      });
    },
    async getAllReportRows() {
      const dataSource = this.reportGrid?.getDataSource();
      if (!dataSource) {
        return [];
      }

      const filter = dataSource.filter();
      const sort = dataSource.sort() || [];
      const rows = [];
      let totalCount = Infinity;
      let skip = 0;

      while (skip < totalCount) {
        const page = await this.loadReportPage({ skip, take: 500, filter, sort });
        if (page.data.length === 0) {
          break;
        }

        rows.push(...page.data);
        totalCount = page.totalCount;
        skip += page.data.length;
      }

      return rows;
    },
    async exportToCsv() {
      const grid = this.reportGrid;
      if (!grid) return;

      const selected = await grid.getSelectedRowsData();
      const rows = Array.isArray(selected) && selected.length
        ? selected
        : this.isServerPagedReport
          ? await this.getAllReportRows()
          : Array.isArray(this.report_dataset) ? this.report_dataset : [];

      const cols = grid
        .getVisibleColumns()
        .filter((c) => !c.command)
        .map((c) => ({ field: c.dataField, caption: c.caption || c.dataField }));

      const escape = (v) => {
        if (v == null) return "";
        const s = String(v);
        const needsQuotes = /[",\r\n]/.test(s);
        const escaped = s.replace(/"/g, '""');
        return needsQuotes ? `"${escaped}"` : escaped;
      };

      const header = cols.map((c) => escape(c.caption)).join(",");
      const lines = rows.map((row) => cols.map((c) => escape(row[c.field])).join(","));
      const metadataLines = this.isHarvestYieldComparisonReport ? this.buildExportMetadataCsvLines(escape) : [];
      const csv = [...metadataLines, header, ...lines].join("\r\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const filename = `${this.templateData.code || "report"}_${this.getCurrentDate()}.csv`;
      saveAs(blob, filename);
    },
    createReportDataSource() {
      const key = this.templateData?.code === "RAPORT_ZILNIC_LUCRARI"
        ? ["work_date_only", "employee", "work_name", "Shift", "company"]
        : undefined;

      return new CustomStore({
        key,
        loadMode: "processed",
        load: (loadOptions) => this.loadReportPage(loadOptions),
      });
    },
    async loadReportPage(loadOptions = {}) {
      this.isGridLoading = true;
      try {
        const sort = Array.isArray(loadOptions.sort) ? loadOptions.sort[0] : loadOptions.sort;
        const rowsResponse = await this.getReportsData({
          name: this.templateData.datasource_SP,
          args: {
            ...this.buildFilterPayload(),
            page_size: Math.min(loadOptions.take || 40, 500),
            page_offset: loadOptions.skip || 0,
            sort_by: sort?.selector || null,
            sort_direction: sort ? (sort.desc ? "DESC" : "ASC") : null,
            grid_filters: this.encodeGridFilters(loadOptions.filter),
          },
        });

        const responsePayload = rowsResponse?.data?.data && !Array.isArray(rowsResponse.data)
          ? rowsResponse.data
          : rowsResponse;
        const rows = Array.isArray(responsePayload)
          ? responsePayload
          : Array.isArray(responsePayload?.data) ? responsePayload.data : [];
        const totalCountField = rows.length
          ? Object.keys(rows[0]).find((field) => field.toLowerCase() === "__total_count")
          : null;
        const totalCount = Number(
          responsePayload?.totalCount
          ?? responsePayload?.total_count
          ?? responsePayload?.meta?.totalCount
          ?? responsePayload?.pagination?.total
          ?? (totalCountField ? rows[0][totalCountField] : null)
          ?? ((loadOptions.skip || 0) + rows.length)
        ) || 0;

        return {
          data: rows.map((row) => {
            const data = { ...row };
            Object.keys(data).forEach((field) => {
              if (field.toLowerCase() === "__total_count") {
                delete data[field];
              }
            });
            return data;
          }),
          totalCount,
        };
      } finally {
        this.isGridLoading = false;
      }
    },
    encodeGridFilters(filter) {
      const filters = [];
      const supportedOperators = new Set(["=", "!=", "contains", "notcontains", "startswith", "endswith", ">", ">=", "<", "<="]);
      const collect = (expression) => {
        if (!Array.isArray(expression)) {
          return;
        }

        if (expression.length === 3 && typeof expression[0] === "string" && typeof expression[1] === "string") {
          const operator = expression[1] === "<>" ? "!=" : expression[1];
          if (supportedOperators.has(operator)) {
            filters.push({ field: expression[0], operator, value: expression[2] });
          }
          return;
        }

        expression.forEach((part) => {
          if (part !== "and" && part !== "or") {
            collect(part);
          }
        });
      };

      collect(filter);
      return filters.length ? btoa(unescape(encodeURIComponent(JSON.stringify(filters)))) : null;
    },
    async ensureFilterDataSourcesLoaded() {
      this.getDepartments({ limit: 9999 });
      this.getSeasons({ limit: 9999 });
      this.getCultures({ limit: 9999 });
    },
    async loadReportData() {
      this.isInitialLoading = true;
      try {
        this.ensureFilterDataSourcesLoaded();

        const columnResponse = await this.getReportColumns({ report_id: this.templateData.id, limit: 500 });
        this.gridColumns = [];
        this.aggregatedColumns = [];
        columnResponse.data.data
          .sort((a, b) => a.order - b.order)
          .forEach((column) => {
            const colSetting = {
              dataField: column.field_name,
              dataType: column.data_type,
              caption: column.title,
              width: column.width,
              visible: column.visible == 1,
              format: this.resolveColumnFormat(column),
            };

            if (this.templateData?.code === "BON_CONSUM_PE_CULTURA") {
              if (column.field_name === "culture_name" && !this.isServerPagedReport) {
                colSetting.groupIndex = 0;
              }

              if (column.field_name === "product_type_group_name" && !this.isServerPagedReport) {
                colSetting.groupIndex = 1;
              }
            }

            this.gridColumns.push(colSetting);

            if (column.aggregate_type && column.aggregate_type !== "none") {
              this.parseAggregateTypes(column.aggregate_type).forEach((aggregateType) => {
                const customYieldPrefix = "custom_yield";
                const customUniqueSumPrefix = "custom_unique_sum";
                const isCustomYield = aggregateType.startsWith(customYieldPrefix);
                const isCustomUniqueSum = aggregateType.startsWith(customUniqueSumPrefix);

                let quantityField = "quantity";
                let surfaceField = "surface";
                let uniqueKeyField = "uuid";
                let uniqueValueField = column.field_name;

                if (isCustomYield) {
                  const parts = aggregateType.split(":");
                  if (parts[1]) {
                    quantityField = parts[1];
                  }
                  if (parts[2]) {
                    surfaceField = parts[2];
                  }
                }

                if (isCustomUniqueSum) {
                  const parts = aggregateType.split(":");
                  if (parts[1]) {
                    uniqueKeyField = parts[1];
                  }
                  if (parts[2]) {
                    uniqueValueField = parts[2];
                  }
                }

                this.aggregatedColumns.push({
                  column: column.field_name,
                  name: `${column.field_name}_${String(aggregateType).replace(/[^a-zA-Z0-9_]/g, "_")}`,
                  summary_type: isCustomYield || isCustomUniqueSum ? "custom" : aggregateType,
                  value_format: column.format_type,
                  show_aggregate_in_footer: column.show_aggregate_in_footer,
                  show_aggregate_in_group: column.show_aggregate_in_group,
                  custom_type: isCustomYield ? customYieldPrefix : isCustomUniqueSum ? customUniqueSumPrefix : null,
                  quantity_field: quantityField,
                  surface_field: surfaceField,
                  unique_key_field: uniqueKeyField,
                  unique_value_field: uniqueValueField,
                });
              });
            }
          });

        const filterResponse = await this.getReportFilters({ report_id: this.templateData.id, limit: 500 });
        this.basicInfoItems = [];
        this.reportFiltersMeta = filterResponse.data.data.sort((a, b) => (Number(a.order) > Number(b.order) ? 1 : -1));

        this.reportFiltersMeta.forEach((filter) => {
          const editorType = this.resolveFilterEditorType(filter);
          this.basicInfoItems.push({
            dataField: filter.field_name,
            label: filter.title,
            dataType: filter.data_type,
            editorType,
            editorOptions: this.resolveFilterEditorOptions(filter, editorType),
            validators: [],
          });

          if (this.formData[filter.field_name] === undefined || this.formData[filter.field_name] === null) {
            this.applyFilterDefaults(filter);
          }
        });

        this.report_data_source = this.isServerPagedReport
          ? this.createReportDataSource()
          : this.report_dataset;
        this.reloadReportDataset();
      } finally {
        // Don't hide the loader here: the grid renders columns/rows on nextTick
        // after this promise resolves, which is the heavy part. Hide the loader
        // when the grid fires content-ready instead (see onGridContentReady).
        this.pendingContentReadyHide = true;
      }
    },
    async reloadReportDataset() {
      if (!this.templateData || !this.templateData.datasource_SP) {
        return;
      }

      if (this.isServerPagedReport) {
        if (this.reportGrid) {
          this.reportGrid.pageIndex(0);
          await this.reportGrid.refresh();
        }
        return;
      }

      this.isGridLoading = true;
      try {
        const payload = this.buildFilterPayload();
        this.report_dataset = await this.getReportsData({ name: this.templateData.datasource_SP, args: payload });
        this.report_data_source = this.report_dataset;
      } finally {
        this.isGridLoading = false;
      }
    },
    buildFilterPayload() {
      const payload = { ...this.formData };

      this.basicInfoItems.forEach((item) => {
        if ((item.dataType === "datetime" || item.dataType === "date") && payload[item.dataField]) {
          payload[item.dataField] = moment(payload[item.dataField]).format("YYYY-MM-DD");
        }

        if (Array.isArray(payload[item.dataField])) {
          payload[item.dataField] = payload[item.dataField].length > 0 ? payload[item.dataField].join(",") : null;
        }
      });

      return payload;
    },
    resolveFilterEditorType(filter) {
      const frontendOptions = filter?.frontend_options || {};
      if (frontendOptions.editor_type) {
        return frontendOptions.editor_type;
      }

      switch (filter.data_type) {
        case "number":
          return "dxNumberBox";
        case "date":
        case "datetime":
          return "dxDateBox";
        case "boolean":
          return "dxCheckBox";
        default:
          return "dxTextBox";
      }
    },
    resolveFilterEditorOptions(filter, editorType) {
      const frontendOptions = filter?.frontend_options || {};
      const baseOptions = {
        stylingMode: "filled",
      };

      if (editorType === "dxDateBox") {
        return {
          ...baseOptions,
          type: "date",
          displayFormat: this.getDefaultDateFormat || "yyyy-MM-dd",
          valueChangeEvent: "change",
          openOnFieldClick: true,
        };
      }

      if (frontendOptions.source === "departments") {
        return {
          ...baseOptions,
          dataSource: this.departments || [],
          displayExpr: "name",
          valueExpr: "uuid",
          showSelectionControls: true,
          searchEnabled: true,
          showClearButton: true,
          applyValueMode: "instantly",
          multiline: false,
        };
      }

      if (frontendOptions.source === "seasons") {
        return {
          ...baseOptions,
          dataSource: this.seasons || [],
          displayExpr: "name",
          valueExpr: "uuid",
          searchEnabled: true,
          showClearButton: false,
        };
      }

      if (frontendOptions.source === "cultures") {
        return {
          ...baseOptions,
          dataSource: this.cultures || [],
          displayExpr: "name",
          valueExpr: "uuid",
          showSelectionControls: true,
          searchEnabled: true,
          showClearButton: true,
          applyValueMode: "instantly",
          multiline: false,
        };
      }

      if (frontendOptions.source === "approved_only") {
        return {
          ...baseOptions,
          dataSource: [
            { value: 0, text: "Nu" },
            { value: 1, text: "Da" },
          ],
          displayExpr: "text",
          valueExpr: "value",
          showClearButton: false,
        };
      }

      if (frontendOptions.source === "transaction_types") {
        return {
          ...baseOptions,
          dataSource: [
            { value: "RECEPTION", text: "Recepție" },
            { value: "SALES", text: "Vânzare" },
            { value: "TRANSFER", text: "Transfer" },
            { value: "REQUALIFICATION", text: "Recalificare" },
            { value: "CONSUM", text: "Consum" },
            { value: "DECOMMISSION", text: "Pierderi din depozitare" },
            { value: "INITIAL_IMPORT", text: "Import inițial" },
            { value: "PRODUCTION", text: "Recoltă" },
          ],
          displayExpr: "text",
          valueExpr: "value",
          showSelectionControls: true,
          searchEnabled: true,
          showClearButton: true,
          applyValueMode: "instantly",
          multiline: false,
        };
      }

      return baseOptions;
    },
    applyFilterDefaults(filter) {
      if (!filter) {
        return;
      }

      const frontendOptions = filter.frontend_options || {};
      if (frontendOptions.default_value !== undefined) {
        this.formData[filter.field_name] = frontendOptions.default_value;
        return;
      }

      if (frontendOptions.default_mode === "max_year" && frontendOptions.source === "seasons") {
        const seasonsList = Array.isArray(this.seasons) ? this.seasons : [];
        const sortedSeasons = seasonsList
          .filter((season) => Number.isFinite(Number(season?.year)))
          .sort((a, b) => Number(b.year) - Number(a.year));

        if (sortedSeasons.length > 0) {
          this.formData[filter.field_name] = sortedSeasons[0].uuid;
        }

        return;
      }

      if ((frontendOptions.multiselect || frontendOptions.editor_type === "dxTagBox") && this.formData[filter.field_name] === undefined) {
        this.formData[filter.field_name] = [];
      }

      const { default_mode: defaultMode, default_unit: defaultUnit, default_offset: defaultOffset } = frontendOptions;
      if (defaultMode !== "relative_to_now" || !defaultUnit || defaultOffset === undefined || defaultOffset === null) {
        return;
      }

      if (filter.data_type !== "date" && filter.data_type !== "datetime") {
        return;
      }

      const unitMap = {
        day: "days",
        week: "weeks",
        month: "months",
        year: "years",
      };
      const momentUnit = unitMap[String(defaultUnit).toLowerCase()] || null;
      if (!momentUnit) {
        return;
      }

      const offsetValue = Number(defaultOffset);
      if (Number.isNaN(offsetValue)) {
        return;
      }

      this.formData[filter.field_name] = moment().add(offsetValue, momentUnit).format("YYYY-MM-DD");
    },
    parseAggregateTypes(aggregateType) {
      return String(aggregateType)
        .split("|")
        .map((value) => value.trim())
        .filter((value) => value && value !== "none");
    },
    formatNumber(value, digits = 2) {
      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) {
        return "0";
      }

      return numericValue.toLocaleString(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
    },
    buildExportMetadataRows() {
      const rows = [
        { label: this.$t("reporting.harvest_yield.labels.report"), value: this.templateData?.name || "" },
        { label: this.$t("reporting.harvest_yield.labels.generated_at"), value: this.getCurrentDate() },
      ];

      this.basicInfoItems.forEach((item) => {
        const value = this.formData[item.dataField];
        if (value === undefined || value === null || value === "") {
          return;
        }

        rows.push({ label: item.label || item.dataField, value: Array.isArray(value) ? value.join(", ") : value });
      });

      if (this.isHarvestYieldComparisonReport) {
        this.summaryCards.forEach((card) => {
          rows.push({ label: card.label, value: card.value });
        });
      }

      return rows;
    },
    buildExportMetadataCsvLines(escape) {
      return this.buildExportMetadataRows().map((row) => `${escape(row.label)},${escape(row.value)}`);
    },
    getReportRowStatus(row) {
      return getRowStatusByReportCode(this.templateData?.code, row, {
        t: this.$t.bind(this),
      });
    },
    buildSummaryCards() {
      return buildSummaryCardsByReportCode(this.templateData?.code, {
        rows: this.summarySourceRows,
        t: this.$t.bind(this),
        formatNumber: this.formatNumber,
      });
    },
    onCalculateCustomSummary(options) {
      const summaryName = String(options?.name || "");
      const summaryConfig = this.aggregatedColumns.find((item) => item.name === summaryName);
      if (!summaryConfig || !summaryConfig.custom_type) {
        return;
      }

      if (summaryConfig.custom_type === "custom_unique_sum") {
        if (options.summaryProcess === "start") {
          options.totalValue = {
            uniqueKeys: {},
            sum: 0,
          };
          return;
        }

        if (options.summaryProcess === "calculate") {
          const row = options.value;

          if (!row || typeof row !== "object" || Array.isArray(row)) {
            const numericValue = Number(row);
            if (!Number.isNaN(numericValue)) {
              options.totalValue.sum += numericValue;
            }
            return;
          }

          const key =
            row?.[summaryConfig.unique_key_field] ??
            row?.parcel_uuid ??
            row?.season_on_parcel_uuid ??
            row?.parcel_name ??
            row?.unique_id ??
            `__fallback_${Object.keys(options.totalValue.uniqueKeys).length}`;

          const normalizedKey = String(key);
          const valueToAdd = Number(row?.[summaryConfig.unique_value_field]) || 0;

          if (!options.totalValue.uniqueKeys[normalizedKey]) {
            options.totalValue.uniqueKeys[normalizedKey] = true;
            options.totalValue.sum += valueToAdd;
          }
          return;
        }

        if (options.summaryProcess === "finalize") {
          options.totalValue = Number(options.totalValue?.sum) || 0;
        }

        return;
      }

      if (summaryConfig.custom_type !== "custom_yield") {
        return;
      }

      if (options.summaryProcess === "start") {
        options.totalValue = {
          quantity: 0,
          surface: 0,
        };
        return;
      }

      if (options.summaryProcess === "calculate") {
        const row = options.value || {};
        options.totalValue.quantity += Number(row[summaryConfig.quantity_field]) || 0;
        options.totalValue.surface += Number(row[summaryConfig.surface_field]) || 0;
        return;
      }

      if (options.summaryProcess === "finalize") {
        const quantity = Number(options.totalValue?.quantity) || 0;
        const surface = Number(options.totalValue?.surface) || 0;
        options.totalValue = surface === 0 ? 0 : quantity / surface;
      }
    },
    refreshDataGrid() {
      this.reloadReportDataset();
      if (!this.isServerPagedReport && this.reportGrid) {
        this.reportGrid.refresh();
      }
    },
    backToList() {
      this.$emit("hideReportGridView");
    },
  },
  mounted() {
    if (this.showModule && this.templateData && this.templateData.id) {
      this.loadReportData();
    }
  },
};
</script>

<style scoped lang="scss">
.back-button {
  width: 34px;
  height: 34px;
  margin-right: 20px;
  border-radius: 50%;
}

.report-data-grid {
  height: calc(95vh - var(--report-grid-offset, 180px));
}

.report-filter-form {
  display: block;
}

.report-filter-form ::v-deep .dx-layout-manager {
  width: 100%;
}

.report-filter-form ::v-deep .dx-field-item,
.report-filter-form ::v-deep .dx-field-item-content,
.report-filter-form ::v-deep .dx-texteditor {
  max-width: 100%;
  width: 100%;
}
</style>
